import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Animated,
  Dimensions,
  ScrollView,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
import {
  selectActiveHelp,
  selectTutoringLoading,
  dismissHelp,
  clearAllHelp,
  toggleHelpOverlay,
  requestContextualHelp,
} from '../../../store/slices/aiTutoringSlice';
import type { ContextualHelp } from '@coffeeplexity/shared/src/types/aiTutoring';
import { logger } from '../../../utils/logger';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

const ContextualHelpOverlay: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const activeHelp = useSelector(selectActiveHelp);
  const loading = useSelector(selectTutoringLoading);

  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedHelp, setSelectedHelp] = useState<ContextualHelp | null>(null);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(screenHeight));

  useEffect(() => {
    if (activeHelp.length > 0 && !showOverlay) {
      setShowOverlay(true);
      setSelectedHelp(activeHelp[0]);
    } else if (activeHelp.length === 0 && showOverlay) {
      handleCloseOverlay();
    }
  }, [activeHelp.length]);

  useEffect(() => {
    if (showOverlay) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(slideAnim, {
          toValue: 0,
          tension: 100,
          friction: 8,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [showOverlay]);

  const handleCloseOverlay = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: screenHeight,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setShowOverlay(false);
      setSelectedHelp(null);
    });
  };

  const handleDismissHelp = (helpId: string) => {
    dispatch(dismissHelp(helpId));
  };

  const handleDismissAll = () => {
    dispatch(clearAllHelp());
  };

  const handleRequestMoreHelp = async () => {
    try {
      await dispatch(
        requestContextualHelp({
          context: {
            screenId: 'tutoring-dashboard',
            userAction: 'request_help',
            currentSkillLevel: 'intermediate',
            strugglingAreas: ['brewing-technique', 'grind-consistency'],
            recentMistakes: ['over-extraction', 'uneven-grind'],
            timeSpentOnTask: 15,
          },
          helpType: 'tip',
        })
      );
    } catch (error) {
      logger.error('Failed to request help:', error);
    }
  };

  const getPriorityColor = (priority: ContextualHelp['priority']) => {
    switch (priority) {
      case 'critical':
        return '#F44336';
      case 'high':
        return '#FF9800';
      case 'medium':
        return '#2196F3';
      case 'low':
        return '#4CAF50';
      default:
        return '#666';
    }
  };

  const getHelpTypeIcon = (helpType: ContextualHelp['helpType']) => {
    switch (helpType) {
      case 'explanation':
        return 'school';
      case 'tip':
        return 'lightbulb';
      case 'warning':
        return 'warning';
      case 'example':
        return 'code';
      case 'next_step':
        return 'arrow-forward';
      default:
        return 'help';
    }
  };

  const renderHelpCard = (
    help: ContextualHelp,
    isSelected: boolean = false
  ) => (
    <TouchableOpacity
      key={help.helpId}
      style={[
        styles.helpCard,
        isSelected && styles.selectedHelpCard,
        { borderLeftColor: getPriorityColor(help.priority) },
      ]}
      onPress={() => setSelectedHelp(help)}
      activeOpacity={0.7}
    >
      <View style={styles.helpHeader}>
        <View style={styles.helpTypeContainer}>
          <Icon
            name={getHelpTypeIcon(help.helpType)}
            size={16}
            color={getPriorityColor(help.priority)}
          />
          <Text
            style={[
              styles.helpType,
              { color: getPriorityColor(help.priority) },
            ]}
          >
            {help.helpType.replace(/_/g, ' ')}
          </Text>
        </View>
        <TouchableOpacity
          style={styles.dismissButton}
          onPress={() => handleDismissHelp(help.helpId)}
        >
          <Icon name='close' size={16} color='#666' />
        </TouchableOpacity>
      </View>

      <Text style={styles.helpTitle}>{help.content.title}</Text>
      <Text
        style={styles.helpMessage}
        numberOfLines={isSelected ? undefined : 2}
      >
        {help.content.message}
      </Text>

      {help.content.actionable && help.content.suggestedActions.length > 0 && (
        <View style={styles.actionsContainer}>
          <Text style={styles.actionsTitle}>Suggested Actions:</Text>
          {help.content.suggestedActions
            .slice(0, isSelected ? undefined : 2)
            .map((action, index) => (
              <View key={index} style={styles.actionItem}>
                <Icon name='check-circle-outline' size={14} color='#4CAF50' />
                <Text style={styles.actionText}>{action}</Text>
              </View>
            ))}
        </View>
      )}

      {help.content.examples.length > 0 && isSelected && (
        <View style={styles.examplesContainer}>
          <Text style={styles.examplesTitle}>Examples:</Text>
          {help.content.examples.map((example, index) => (
            <Text key={index} style={styles.exampleText}>
              • {example}
            </Text>
          ))}
        </View>
      )}
    </TouchableOpacity>
  );

  const renderFloatingHelpButton = () => {
    if (activeHelp.length === 0) return null;

    return (
      <TouchableOpacity
        style={styles.floatingHelpButton}
        onPress={() => setShowOverlay(true)}
        activeOpacity={0.8}
      >
        <Icon name='help' size={24} color='#fff' />
        <View style={styles.helpBadge}>
          <Text style={styles.helpBadgeText}>{activeHelp.length}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  if (!showOverlay && activeHelp.length === 0) {
    return null;
  }

  return (
    <>
      {renderFloatingHelpButton()}

      <Modal
        visible={showOverlay}
        transparent
        animationType='none'
        onRequestClose={handleCloseOverlay}
      >
        <View style={styles.overlay}>
          <Animated.View style={[styles.backdrop, { opacity: fadeAnim }]}>
            <TouchableOpacity
              style={StyleSheet.absoluteFill}
              onPress={handleCloseOverlay}
              activeOpacity={1}
            />
          </Animated.View>

          <Animated.View
            style={[
              styles.helpContainer,
              {
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            <View style={styles.helpOverlayHeader}>
              <View style={styles.headerLeft}>
                <Icon name='help-center' size={24} color='#007AFF' />
                <Text style={styles.overlayTitle}>Contextual Help</Text>
              </View>
              <View style={styles.headerRight}>
                <TouchableOpacity
                  style={styles.headerButton}
                  onPress={handleDismissAll}
                >
                  <Text style={styles.headerButtonText}>Clear All</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.headerButton}
                  onPress={handleCloseOverlay}
                >
                  <Icon name='close' size={24} color='#666' />
                </TouchableOpacity>
              </View>
            </View>

            <ScrollView
              style={styles.helpScrollView}
              contentContainerStyle={styles.helpScrollContent}
              showsVerticalScrollIndicator={false}
            >
              {activeHelp.map(help =>
                renderHelpCard(help, selectedHelp?.helpId === help.helpId)
              )}

              {activeHelp.length === 0 && (
                <View style={styles.noHelpContainer}>
                  <Icon name='help-outline' size={48} color='#ccc' />
                  <Text style={styles.noHelpText}>
                    No active help available
                  </Text>
                  <TouchableOpacity
                    style={styles.requestHelpButton}
                    onPress={handleRequestMoreHelp}
                    disabled={loading.help}
                  >
                    <Icon name='add' size={20} color='#007AFF' />
                    <Text style={styles.requestHelpText}>
                      {loading.help ? 'Requesting...' : 'Request Help'}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  floatingHelpButton: {
    position: 'absolute',
    bottom: 100,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#007AFF',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
  },
  helpBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#F44336',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  helpContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: screenHeight * 0.8,
    paddingBottom: 20,
  },
  helpOverlayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  overlayTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a1a',
    marginLeft: 8,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerButton: {
    padding: 4,
  },
  headerButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#007AFF',
  },
  helpScrollView: {
    flex: 1,
  },
  helpScrollContent: {
    padding: 20,
    gap: 16,
  },
  helpCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    padding: 16,
    borderLeftWidth: 4,
  },
  selectedHelpCard: {
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  helpHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  helpTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  helpType: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize',
    marginLeft: 4,
  },
  dismissButton: {
    padding: 4,
  },
  helpTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 8,
  },
  helpMessage: {
    fontSize: 14,
    color: '#1a1a1a',
    lineHeight: 20,
    marginBottom: 12,
  },
  actionsContainer: {
    marginBottom: 12,
  },
  actionsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
    marginBottom: 6,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  actionText: {
    fontSize: 12,
    color: '#1a1a1a',
    marginLeft: 6,
    flex: 1,
  },
  examplesContainer: {
    marginTop: 8,
  },
  examplesTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
    marginBottom: 6,
  },
  exampleText: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2,
  },
  noHelpContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  noHelpText: {
    fontSize: 16,
    color: '#666',
    marginTop: 12,
    marginBottom: 20,
  },
  requestHelpButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#007AFF',
    gap: 8,
  },
  requestHelpText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#007AFF',
  },
});

export default ContextualHelpOverlay;
